import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { formatMalaysiaDateTimeStamp } from '../../utils/dateTime';
import { BarList, Table } from '../reports/StatistikParts';

// Statistik i-WARAN: the warrant counterpart of Laporan dan Data Statistik for complaints.
const emptySummary = {
    status_labels: {},
    stage_labels: {},
    totals: { by_status: {}, by_stage: {} },
    by_district: [],
    by_result: [],
    without_result: 0,
    by_execution_count: [],
    by_case_type: [],
    by_court: [],
};

const emptyFilters = { district: '', fromDate: '', toDate: '' };

const WaranStatistikReport = () => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const token = localStorage.getItem('token');
    const role = String(localStorage.getItem('role') || '').trim().toLowerCase();
    const isDistrictOfficer = role === 'pegawai_daerah';
    const [isLoading, setIsLoading] = useState(true);
    const [isExporting, setIsExporting] = useState(false);
    const [error, setError] = useState('');
    const [districtOptions, setDistrictOptions] = useState([]);
    const [summary, setSummary] = useState(emptySummary);
    const [filters, setFilters] = useState(emptyFilters);
    // Only the latest request may update the page; an older, slower response must not overwrite it.
    const latestRequestRef = useRef(0);

    useEffect(() => {
        if (!apiUrl) return;
        axios.get(`${apiUrl}/districts`)
            .then((response) => setDistrictOptions(response?.data?.data || []))
            .catch(() => setDistrictOptions([]));
    }, [apiUrl]);

    const params = useMemo(() => ({
        ...(filters.district ? { district_id: filters.district } : {}),
        ...(filters.fromDate ? { from_date: filters.fromDate } : {}),
        ...(filters.toDate ? { to_date: filters.toDate } : {}),
    }), [filters]);

    const loadSummary = () => {
        if (!apiUrl) {
            setError('API URL tidak diset.');
            setIsLoading(false);
            return;
        }
        const requestId = latestRequestRef.current + 1;
        latestRequestRef.current = requestId;
        setIsLoading(true);
        setError('');
        axios.get(`${apiUrl}/i-waran/report/statistik`, {
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
            params,
        })
            .then((response) => {
                if (requestId === latestRequestRef.current) setSummary({ ...emptySummary, ...(response?.data?.data || {}) });
            })
            .catch((err) => {
                if (requestId === latestRequestRef.current) setError(err?.response?.data?.message || 'Gagal memuatkan statistik i-WARAN.');
            })
            .finally(() => {
                if (requestId === latestRequestRef.current) setIsLoading(false);
            });
    };

    useEffect(() => {
        loadSummary();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiUrl, params]);

    const handleExport = async () => {
        if (!apiUrl) return;
        setIsExporting(true);
        try {
            const response = await axios.get(`${apiUrl}/i-waran/report/statistik/export/xlsx`, {
                headers: token ? { Authorization: `Bearer ${token}` } : undefined,
                params,
                responseType: 'blob',
            });
            const disposition = response?.headers?.['content-disposition'] || '';
            const match = disposition.match(/filename="?([^"]+)"?/i);
            const filename = match?.[1] || `statistik-iwaran-${formatMalaysiaDateTimeStamp()}.xlsx`;
            const url = window.URL.createObjectURL(new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setError('Gagal export Excel statistik i-WARAN.');
        } finally {
            setIsExporting(false);
        }
    };

    const totals = summary.totals || emptySummary.totals;
    const byStatus = totals.by_status || {};
    const byStage = totals.by_stage || {};
    // Draf only shows when there are draft warrants, to keep the table narrow.
    const statusColumns = Object.entries(summary.status_labels || {})
        .filter(([key]) => key !== 'draf' || Number(byStatus.draf || 0) > 0);
    const stageColumns = Object.entries(summary.stage_labels || {});
    const caseTypeRows = summary.by_case_type || [];
    const courtRows = summary.by_court || [];
    const sum = (rows, key) => rows.reduce((total, row) => total + Number(row[key] || 0), 0);
    const districtColumn = { key: 'district_name', label: 'Daerah', width: '1fr', render: (row) => row.district_name };

    return (
        <div className="app-section">
            <div className="app-section-header">
                <div>
                    <div className="app-section-eyebrow">PENGURUSAN LAPORAN</div>
                    <h2>Statistik i-WARAN</h2>
                    <p>Status dan peringkat waran mengikut daerah, hasil dan bilangan pelaksanaan, jenis kes dan mahkamah.</p>
                </div>
                <Link className="app-button app-button-ghost" to="/app/i-waran/report">
                    Kembali ke Laporan i-WARAN
                </Link>
            </div>

            <div className="app-card">
                <div className="app-tab-panel">
                    <div className="app-form-grid">
                        {!isDistrictOfficer && (
                            <label className="app-form-field">
                                <span>Daerah</span>
                                <select id="waran-statistik-district" value={filters.district} onChange={(e) => setFilters((prev) => ({ ...prev, district: e.target.value }))}>
                                    <option value="">Semua</option>
                                    {districtOptions.map((d) => (
                                        <option key={d.id} value={d.id}>{d.name}</option>
                                    ))}
                                </select>
                            </label>
                        )}
                        <label className="app-form-field">
                            <span>Dari Tarikh Terima</span>
                            <input id="waran-statistik-from" type="date" value={filters.fromDate} onChange={(e) => setFilters((prev) => ({ ...prev, fromDate: e.target.value }))} />
                        </label>
                        <label className="app-form-field">
                            <span>Hingga Tarikh Terima</span>
                            <input id="waran-statistik-to" type="date" value={filters.toDate} onChange={(e) => setFilters((prev) => ({ ...prev, toDate: e.target.value }))} />
                        </label>
                        <div className="app-form-actions app-span-full">
                            <button type="button" className="app-button app-button-ghost" onClick={() => setFilters(emptyFilters)}>
                                Reset
                            </button>
                            <button type="button" className="app-button app-button-ghost" onClick={handleExport} disabled={isExporting}>
                                {isExporting ? 'Sedang Export...' : 'Export Excel'}
                            </button>
                            <button type="button" className="app-button" onClick={loadSummary}>
                                Refresh
                            </button>
                        </div>
                    </div>
                    <p className="app-muted" style={{ margin: '.5rem 0 0', fontSize: '.85rem' }}>
                        Waran ditapis mengikut Tarikh / Masa Terima waran.{isDistrictOfficer ? ' Hanya waran daerah anda dipaparkan.' : ''}
                    </p>

                    {error && <div className="app-empty">{error}</div>}
                    {isLoading && <div className="app-empty">Memuatkan statistik...</div>}

                    {!isLoading && !error && (
                        <div className="app-report" style={{ marginTop: '1rem' }}>
                            <div className="app-report-kpis">
                                <div className="app-report-kpi"><span>Jumlah Waran</span><strong>{totals.total ?? 0}</strong></div>
                                <div className="app-report-kpi"><span>Berjaya</span><strong>{byStatus.berjaya ?? 0}</strong></div>
                                <div className="app-report-kpi"><span>Tidak Berjaya</span><strong>{byStatus.tidak_berjaya ?? 0}</strong></div>
                                <div className="app-report-kpi"><span>Dalam Proses</span><strong>{byStatus.dalam_proses ?? 0}</strong></div>
                                <div className="app-report-kpi"><span>Kembalian</span><strong>{byStatus.kembalian ?? 0}</strong></div>
                                <div className="app-report-kpi"><span>Belum Dilaksanakan</span><strong>{totals.not_executed ?? 0}</strong></div>
                            </div>

                            <div className="app-report-card">
                                <h4>Status Waran Mengikut Daerah</h4>
                                <Table
                                    columns={[
                                        districtColumn,
                                        ...statusColumns.map(([key, label]) => ({ key, label, render: (row) => row.status?.[key] ?? 0 })),
                                        { key: 'total', label: 'Jumlah' },
                                    ]}
                                    rows={summary.by_district || []}
                                    rowKey={(row) => `status-${row.district_id || 'none'}`}
                                    footer={{
                                        district_name: 'Jumlah',
                                        ...Object.fromEntries(statusColumns.map(([key]) => [key, byStatus[key] ?? 0])),
                                        total: totals.total ?? 0,
                                    }}
                                />
                            </div>

                            <div className="app-report-card">
                                <h4>Peringkat Aliran Kerja Mengikut Daerah</h4>
                                <Table
                                    columns={[
                                        districtColumn,
                                        ...stageColumns.map(([key, label]) => ({ key, label, render: (row) => row.stage?.[key] ?? 0 })),
                                        { key: 'total', label: 'Jumlah' },
                                    ]}
                                    rows={summary.by_district || []}
                                    rowKey={(row) => `stage-${row.district_id || 'none'}`}
                                    footer={{
                                        district_name: 'Jumlah',
                                        ...Object.fromEntries(stageColumns.map(([key]) => [key, byStage[key] ?? 0])),
                                        total: totals.total ?? 0,
                                    }}
                                />
                            </div>

                            <div className="app-report-card">
                                <h4>Bilangan Pelaksanaan</h4>
                                <BarList rows={summary.by_execution_count || []} emptyText="Tiada waran untuk tapisan semasa." />
                            </div>

                            <div className="app-report-card">
                                <h4>Hasil Pelaksanaan</h4>
                                <BarList rows={summary.by_result || []} emptyText="Tiada hasil pelaksanaan direkodkan untuk tapisan semasa." />
                                {Number(summary.without_result || 0) > 0 && (
                                    <p className="app-muted" style={{ margin: '.5rem 0 0', fontSize: '.85rem' }}>
                                        {summary.without_result} waran belum direkodkan hasil pelaksanaan dan tidak dikira dalam senarai ini.
                                    </p>
                                )}
                            </div>

                            <div className="app-report-card">
                                <h4>Waran Mengikut Jenis Kes</h4>
                                {caseTypeRows.length ? (
                                    <Table
                                        columns={[
                                            { key: 'category', label: 'Kategori', width: '0.35fr', render: (row) => row.category || '-' },
                                            { key: 'code', label: 'Kod', width: '0.5fr', render: (row) => row.code || '-' },
                                            { key: 'name', label: 'Jenis Kes', width: '1.6fr', render: (row) => row.name },
                                            { key: 'total', label: 'Bilangan', width: '0.35fr' },
                                        ]}
                                        rows={caseTypeRows}
                                        rowKey={(row, index) => `case-type-${row.id || `none-${index}`}`}
                                        footer={{ name: 'Jumlah', total: sum(caseTypeRows, 'total') }}
                                    />
                                ) : (
                                    <div className="app-empty">Tiada waran untuk tapisan semasa.</div>
                                )}
                            </div>

                            <div className="app-report-card">
                                <h4>Waran Mengikut Mahkamah</h4>
                                {courtRows.length ? (
                                    <Table
                                        columns={[
                                            { key: 'name', label: 'Mahkamah', width: '1.6fr', render: (row) => row.name },
                                            { key: 'berjaya', label: 'Berjaya', width: '0.4fr' },
                                            { key: 'total', label: 'Jumlah', width: '0.4fr' },
                                        ]}
                                        rows={courtRows}
                                        rowKey={(row, index) => `court-${row.id || `none-${index}`}`}
                                        footer={{ name: 'Jumlah', berjaya: sum(courtRows, 'berjaya'), total: sum(courtRows, 'total') }}
                                    />
                                ) : (
                                    <div className="app-empty">Tiada waran untuk tapisan semasa.</div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default WaranStatistikReport;
